import { KeycloakInitOptions } from 'keycloak-js';

const originalPathStorageKey = 'keycloakServerLoginFlow.originalPath';
const defaultAuthenticatedPath = '/devices';
export const keycloakServerLoginCallbackPath = '/auth/keycloak/callback';

export interface KeycloakServerLoginTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
}

export type KeycloakServerLoginFlow = 'jwt' | 'session';

export type KeycloakServerLoginCallbackResult =
  | { kind: 'jwt'; loginResult: string }
  | { kind: 'session' }
  | { kind: 'missing' };

type BrowserLocation = Pick<
  Location,
  'origin' | 'pathname' | 'search' | 'hash'
>;

export function buildServerLoginUrl(
  restUrl: string,
  returnUrl: string,
  realm: string,
): string {
  const loginUrl = new URL(`${restUrl.replace(/\/$/, '')}/auth/keycloak/login`);
  loginUrl.searchParams.set('returnUrl', returnUrl);
  loginUrl.searchParams.set('realm', realm);
  return loginUrl.toString();
}

export function buildCallbackReturnUrl(
  location: Pick<BrowserLocation, 'origin'>,
): string {
  return new URL(keycloakServerLoginCallbackPath, location.origin).toString();
}

export function getCurrentSpaPath(location: BrowserLocation): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function getLoginResult(search: string): string | null {
  return new URLSearchParams(search).get('loginResult');
}

export function getSessionLoginResult(search: string): string | null {
  return new URLSearchParams(search).get('sessionLogin');
}

export function getLoginError(search: string): string | null {
  return new URLSearchParams(search).get('error');
}

export function getServerLoginCallbackResult(
  search: string,
  loginFlow: KeycloakServerLoginFlow,
): KeycloakServerLoginCallbackResult {
  if (loginFlow === 'jwt') {
    const loginResult = getLoginResult(search);

    return loginResult ? { kind: 'jwt', loginResult } : { kind: 'missing' };
  }

  return getSessionLoginResult(search) === 'success'
    ? { kind: 'session' }
    : { kind: 'missing' };
}

export function isInvalidStateLoginError(search: string): boolean {
  return getLoginError(search) === 'invalidState';
}

export function isServerLoginCallback(
  location: Pick<BrowserLocation, 'pathname'>,
): boolean {
  return location.pathname === keycloakServerLoginCallbackPath;
}

export function prepareServerLoginRedirect(input: {
  restUrl: string;
  realm: string;
  location: BrowserLocation;
  storage: Storage;
}): string {
  const { restUrl, realm, location, storage } = input;

  if (!isServerLoginCallback(location)) {
    storage.setItem(originalPathStorageKey, getCurrentSpaPath(location));
  }

  return buildServerLoginUrl(restUrl, buildCallbackReturnUrl(location), realm);
}

export function consumeOriginalPath(storage: Storage): string {
  const originalPath =
    storage.getItem(originalPathStorageKey) || defaultAuthenticatedPath;

  storage.removeItem(originalPathStorageKey);

  return originalPath;
}

export function toKeycloakInitOptions(
  tokenResponse: KeycloakServerLoginTokenResponse,
): KeycloakInitOptions {
  return {
    token: tokenResponse.access_token,
    refreshToken: tokenResponse.refresh_token,
    idToken: tokenResponse.id_token,
    checkLoginIframe: false,
  };
}
